import React, {CSSProperties, ReactElement, FC} from 'react';

import {ITableHeaderCell, TTableHeaderStructure} from 'components/report-configuration/tabs/report-table-settings/modals/excel-header-structure-loader-modal/excel-header-structure-loader-modal.types';
import {IFieldMeta} from 'entities/metadata';
import {TEntity} from 'modules/data';
import {EPostgresStringType} from 'shared/types/postgres';
import {TABLE_REPORT_URL_FIELD_NAME} from 'store/slices/table-report-slice/table-report-slice-constants';
import {TTableReportColumn} from 'store/slices/table-report-slice/table-report-slice-types';

interface INonbreakingSpacesProps {
    children?: string | number;
}

export const NonbreakingSpaces: FC<INonbreakingSpacesProps> = ({
    children,
}) => {
    if (!children) return null;
    if (typeof children === 'object') return children;

    const lineWithNonbreakingSpaces = children.toString().split('&nbsp;');
    const lineComponent = (() => {
        const ln: (ReactElement | string)[] = [];
        lineWithNonbreakingSpaces.forEach((l, i) => {
            if (i !== lineWithNonbreakingSpaces.length - 1) {
                ln.push(l);
                ln.push(<>&nbsp;</>);
            } else ln.push(l);
        });
        return <>{ln}</>;
    })();

    return lineComponent;
};

export const isColumnTypeOfString = (
    tableReportAttributes: Record<string, string> | undefined,
    column: TTableReportColumn | undefined,
) => {
    if (!column) return false;
    const fieldType = tableReportAttributes?.[column.dataIndex];
    if (!fieldType) return false;
    return Object.values(EPostgresStringType).includes(fieldType as EPostgresStringType);
};

export const getColumnHeaderWidthStyles = (
    column: TTableReportColumn | undefined,
) => {
    const {width = '30vw'} = column ?? {};

    return {
        minWidth: 'min-content',
        maxWidth: width,
        width: column?.width ? width : undefined,
    } as CSSProperties;
};

export const getColumnWidthStyles = (
    tableReportAttributes: Record<string, string> | undefined,
    column: TTableReportColumn | undefined,
) => {
    const styles: CSSProperties = {};
    const {width, enabledTextWrapping} = column ?? {};

    if (width) {
        styles.width = width;
        styles.minWidth = width;
        styles.maxWidth = width;
    }
    if (!enabledTextWrapping) {
        styles.wordBreak = 'unset';
        styles.maxWidth = 'unset';
        styles.whiteSpace = 'nowrap';
    } else {
        styles.wordBreak = 'break-word';
    }

    if (isColumnTypeOfString(tableReportAttributes, column)) {
        styles.maxWidth = 'max(350px, calc(30vw))';
        if (width) {
            styles.maxWidth = `min(${width}, calc(30vw))`;
        }
    }
    return styles;
};

export const isCustomColumn = (dataIndex: string) => dataIndex.startsWith('custom_attribute');

export const extractIndexesFromKey = (key: string) => {
    const indexesRegexp = /^(\d*)-(\d*)-(.*)$/;
    const execResult = indexesRegexp.exec(key.replace(/[\r\n]/gm, ''));
    if (!execResult) return undefined;
    if (execResult.length === 4) {
        const l = execResult.length;
        return {
            row: Number(execResult[l - 3]),
            column: Number(execResult[l - 2]),
            title: execResult[l - 1],
        };
    }
    return undefined;
};

export const getTreeMaxColumnIndex = (tree: TTableHeaderStructure): number | undefined => {
    let maxColIndex = 0;

    JSON.stringify(tree, (_, nestedValue) => {
        if (nestedValue?.key) {
            const {column: colIndex} = extractIndexesFromKey(nestedValue?.key) ?? {};
            if (colIndex && colIndex > maxColIndex) {
                maxColIndex = colIndex;
            }
        }
        return nestedValue;
    });

    return maxColIndex > 0 ? maxColIndex : undefined;
};

interface IMultiRowHeaderCellStyleEntity {
    borderClassname: string;
    style?: CSSProperties;
}

interface IGetMultiRowHeaderCellStyleArgs {
    cellRowIndex: number | undefined;
    cellColumnIndex: number | undefined;
    firstRowLastColumnIndex: number | undefined;
    maxColumnIndex: number | undefined;
    borderColor?: string;
}

export const getMultiRowHeaderCellStyle = ({
    cellRowIndex,
    cellColumnIndex,
    firstRowLastColumnIndex,
    maxColumnIndex,
    borderColor,
}: IGetMultiRowHeaderCellStyleArgs): IMultiRowHeaderCellStyleEntity => {
    const borderClassname = cellColumnIndex !== undefined && firstRowLastColumnIndex !== undefined
        && (cellColumnIndex === firstRowLastColumnIndex || cellColumnIndex === maxColumnIndex)
        ? 'own-borders' : 'no-radius';

    const borderStyle = `1px solid ${borderColor ?? '#E0E4F0'}`;
    const isTopRightCell = cellRowIndex === 0 && cellColumnIndex === firstRowLastColumnIndex;

    return {
        borderClassname,
        style: {
            borderRight: borderStyle,
            borderBottom: borderStyle,
            borderTopRightRadius: isTopRightCell ? '5px' : '0',
        },
    };
};

interface IColumnToPutInHeaderRule {
    isLast?: boolean;
    isFirst?: boolean;
    putAfterAttribute?: string;
    dataIndex?: string;
}

export const addLinkColumnsToHeaderStructure = (
    headerStructure: TTableHeaderStructure | undefined,
    columns: TTableReportColumn[] | undefined,
) => {
    const CELL_HEIGHT = 2; // todo: надо определять динамически в зависимости от самой глубокой вложенности

    const columnsToPutInHeaderRules: IColumnToPutInHeaderRule[] = [];
    columns?.forEach((col, index) => {
        if (col.dataIndex.startsWith(TABLE_REPORT_URL_FIELD_NAME)) {
            const previousColumn = columns[index - 1];
            columnsToPutInHeaderRules.push({
                dataIndex: col.dataIndex,
                isFirst: !index,
                isLast: index === columns.length - 1,
                putAfterAttribute: !previousColumn ? undefined : previousColumn.dataIndex,
            });
        }
    });

    const headerStructureWithLinks = [
        ...columnsToPutInHeaderRules.filter(rule => rule.isFirst).map(rule => ({
            key: `0-0-${rule.dataIndex}`,
            title: rule.dataIndex,
            attribute: rule.dataIndex,
            cellHeight: CELL_HEIGHT,
        } as ITableHeaderCell)),
        ...headerStructure ?? [],
        ...columnsToPutInHeaderRules.filter(rule => rule.isLast).map(rule => ({
            key: `0-0-${rule.dataIndex}`,
            title: rule.dataIndex,
            attribute: rule.dataIndex,
            cellHeight: CELL_HEIGHT,
        } as ITableHeaderCell)),
    ];

    const rulesToPut = columnsToPutInHeaderRules.filter( // Справа от заданного столбца
        rule => !!rule.putAfterAttribute,
    );

    rulesToPut.forEach(rule => {
        const columnIndex = headerStructureWithLinks.findIndex(item => item.attribute === rule.putAfterAttribute);
        if (columnIndex !== -1) {
            headerStructureWithLinks.splice(columnIndex + 1, 0, {
                key: `0-0-${rule.dataIndex}`,
                title: rule.dataIndex,
                attribute: rule.dataIndex,
                cellHeight: CELL_HEIGHT,
            } as ITableHeaderCell);
        }
    });
    return headerStructureWithLinks;
};

export const getColumnRedirectParams = (
    field: IFieldMeta,
    record: TEntity,
) => {
    if (field.isNeedToRedirect) {
        return [{name: 'entity', value: record[field.isNeedToRedirect] as string}];
    }
    if (field.redirectOnCondition) {
        const key = field.redirectOnCondition.to || 'id';
        if (String(record[field.redirectOnCondition.target]) === field.redirectOnCondition.value) {
            return [{name: 'entity', value: record[key] as string}];
        }
    }

    return null;
};
